<template>
  <section class="cutout-workspace">
    <div class="cutout-grid">
      <aside class="cutout-panel">
        <div class="control-heading">
          <span class="control-icon"><i class="el-icon-scissors"></i></span>
          <div><strong>框选抠图</strong><small>圈出想要的物体，复杂画面也能准确选择</small></div>
        </div>

        <label
          class="cutout-upload"
          :class="{ filled: sourceUrl, dragging }"
          @dragenter.prevent="dragging = true"
          @dragover.prevent="dragging = true"
          @dragleave.prevent="dragging = false"
          @drop.prevent="handleDrop"
        >
          <input type="file" accept="image/png,image/jpeg,image/webp" :disabled="processing" @change="handleFileInput" />
          <template v-if="sourceUrl">
            <img :src="sourceUrl" alt="待抠图的原始图片" />
            <span><i class="el-icon-refresh"></i> 更换原图</span>
          </template>
          <template v-else>
            <i class="el-icon-upload2"></i>
            <strong>点击上传或将图片拖到这里</strong>
            <small>支持 JPG、PNG、WebP，文件最大 7MB</small>
          </template>
        </label>

        <div v-if="sourceWidth" class="source-meta">
          <span><small>图片尺寸</small><strong>{{ sourceWidth }} × {{ sourceHeight }}</strong></span>
          <span><small>文件大小</small><strong>{{ formattedFileSize }}</strong></span>
          <span><small>输出格式</small><strong>透明 PNG</strong></span>
        </div>

        <div v-if="sourceUrl" class="target-guide" :class="{ ready: targetBox }">
          <span><i :class="targetBox ? 'el-icon-circle-check' : 'el-icon-crop'"></i></span>
          <div>
            <strong>{{ targetBox ? '已框选要抠出的物体' : '请在右侧原图上框住目标' }}</strong>
            <small>{{ targetBox ? '重新拖动可以改选，确认后开始抠图' : '从目标一角拖到另一角，不需要沿边缘描线' }}</small>
          </div>
          <button v-if="targetBox && !processing" type="button" @click="clearTarget">清除</button>
        </div>

        <div class="privacy-note">
          <i class="el-icon-lock"></i>
          <span><strong>原图保护</strong><small>云端只识别主体遮罩，颜色、文字和材质均取自原图</small></span>
        </div>

        <button class="cutout-submit" type="button" :disabled="!file || !targetBox || processing" @click="cutout">
          <i :class="processing ? 'el-icon-loading' : 'el-icon-scissors'"></i>
          <span><strong>{{ submitTitle }}</strong><small>{{ processing ? '请稍候，不要关闭页面' : targetBox ? 'AI 会在框内识别完整主体轮廓' : '先在右侧图片中框住一个物体' }}</small></span>
        </button>
        <p v-if="errorMessage" class="cutout-error" role="alert"><i class="el-icon-warning-outline"></i>{{ errorMessage }}</p>
      </aside>

      <main class="cutout-preview">
        <header>
          <div><strong>{{ resultUrl ? '透明效果预览' : '在原图中框选目标' }}</strong><small>{{ previewHint }}</small></div>
          <div v-if="resultUrl" class="preview-actions">
            <button type="button" @click="reselect"><i class="el-icon-location-outline"></i> 修改目标</button>
            <WorkflowTuningButton
              variant="button"
              :image-url="resultUrl"
              prompt="基于当前透明背景主体继续精修，保持主体轮廓、比例和透明背景关系不变。"
              module-name="一键抠图"
            />
            <button type="button" class="primary" @click="download"><i class="el-icon-download"></i> 下载透明 PNG</button>
          </div>
        </header>

        <div v-if="!sourceUrl" class="preview-empty">
          <span><i class="el-icon-scissors"></i></span>
          <strong>上传图片开始抠图</strong>
          <small>上传后用矩形框住想保留的物体，不需要手工描边</small>
        </div>
        <div v-else class="preview-stage">
          <div
            class="target-surface"
            :class="{ selectable: !resultUrl && !processing }"
            role="button"
            :tabindex="resultUrl || processing ? -1 : 0"
            :aria-label="targetBox ? '已框选目标，可重新拖动改选；方向键可移动选框' : '拖动框选图片中想要抠出的物体'"
            @pointerdown="startTargetSelection"
            @pointermove="moveTargetSelection"
            @pointerup="finishTargetSelection"
            @pointercancel="cancelTargetSelection"
            @keydown="handleTargetKeydown"
          >
            <img :src="resultUrl || sourceUrl" :alt="resultUrl ? '透明背景抠图结果' : '待标记的原始图片'" />
            <span
              v-if="targetBox && !resultUrl"
              class="target-marker"
              :style="targetBoxStyle"
              aria-hidden="true"
            ><span>目标范围</span><i></i></span>
          </div>
          <span class="stage-label" :class="{ completed: resultUrl }">
            <i :class="resultUrl ? 'el-icon-circle-check' : processing ? 'el-icon-loading' : targetBox ? 'el-icon-circle-check' : 'el-icon-crop'"></i>
            {{ resultUrl ? '透明背景结果' : processing ? '正在识别框选目标' : targetBox ? '目标已框选，可重新拖动' : '请拖动框住要抠出的物体' }}
          </span>
        </div>

        <footer v-if="resultUrl" class="result-summary">
          <span><i class="el-icon-circle-check"></i></span>
          <div><strong>抠图已完成</strong><small>输出尺寸 {{ sourceWidth }} × {{ sourceHeight }}，可直接用于设计、商品展示和二次编辑</small></div>
        </footer>
      </main>
    </div>

    <section class="cutout-history" aria-label="抠图记录">
      <header class="cutout-history-head">
        <div><strong>抠图记录</strong><small>每次抠图都会自动保留原图和透明 PNG，可随时预览与下载</small></div>
        <button type="button" :disabled="historyLoading" aria-label="刷新抠图记录" @click="loadHistory">
          <i :class="historyLoading ? 'el-icon-loading' : 'el-icon-refresh'"></i>{{ historyLoading ? '正在刷新' : '刷新记录' }}
        </button>
      </header>
      <div v-if="history.length" class="cutout-history-grid">
        <article v-for="record in history" :key="record.id">
          <div class="history-comparison">
            <button type="button" :disabled="!record.source" aria-label="查看抠图原图" @click="previewHistoryImage(record, 'source')">
              <img v-if="record.source" :src="$ossImageUrl(record.source, 'thumbnail')" alt="抠图处理前原图" loading="lazy" />
              <span v-else><i class="el-icon-picture-outline"></i>原图不可用</span>
              <small>原图</small>
            </button>
            <button type="button" :disabled="!record.result" aria-label="查看透明背景结果" @click="previewHistoryImage(record, 'result')">
              <img v-if="record.result" :src="$ossImageUrl(record.result, 'thumbnail')" alt="透明背景抠图结果" loading="lazy" />
              <span v-else><i class="el-icon-warning-outline"></i>结果不可用</span>
              <small>透明 PNG</small>
            </button>
            <em>已完成</em>
          </div>
          <div class="history-dimension"><i class="el-icon-full-screen"></i><span>输出尺寸</span><strong>{{ record.dimensions }}</strong></div>
          <footer>
            <div><strong>一键抠图 · 透明背景</strong><small>{{ formatTime(record.createdAt) }}</small></div>
            <div class="history-actions">
              <WorkflowTuningButton
                v-if="record.result"
                variant="button"
                icon-only
                :image-url="record.result"
                prompt="基于当前透明背景主体继续精修，保持主体轮廓、比例和透明背景关系不变。"
                module-name="一键抠图"
              />
              <button type="button" :disabled="!record.result || isDeletingHistory(record.id)" aria-label="下载该抠图结果" @click="downloadHistory(record)"><i class="el-icon-download"></i></button>
              <button type="button" class="danger" :disabled="isDeletingHistory(record.id)" aria-label="删除该抠图记录" @click="removeHistory(record)"><i :class="isDeletingHistory(record.id) ? 'el-icon-loading' : 'el-icon-delete'"></i></button>
            </div>
          </footer>
        </article>
      </div>
      <div v-else class="cutout-history-empty">
        <i :class="historyLoading ? 'el-icon-loading' : 'el-icon-time'"></i>
        <strong>{{ historyLoading ? '正在加载抠图记录' : '还没有抠图记录' }}</strong>
        <small>{{ historyError || '完成第一次抠图后，原图和透明 PNG 会自动显示在这里。' }}</small>
      </div>
    </section>
  </section>
</template>

<script>
import { apiRequest, downloadResource, postMultipartResource } from '@/services/api'

const MAX_FILE_BYTES = 7 * 1024 * 1024

export default {
  name: 'OneClickCutout',
  data() {
    return {
      file: null,
      sourceUrl: '',
      resultUrl: '',
      sourceWidth: 0,
      sourceHeight: 0,
      targetBox: null,
      selectionStart: null,
      selectionPointerId: null,
      dragging: false,
      processing: false,
      errorMessage: '',
      history: [],
      historyLoading: false,
      historyError: '',
      deletingHistoryIds: []
    }
  },
  computed: {
    submitTitle() {
      if (this.processing) return '正在识别框选目标'
      return this.targetBox ? '抠出这个物体' : '请先框选目标'
    },
    previewHint() {
      if (this.resultUrl) return '抠图完成，可下载透明 PNG'
      if (this.processing) return '系统正在识别选框内的完整物体轮廓'
      return this.targetBox ? '目标已框选；重新拖动可以改选' : '大致框住整个物体即可，不需要精确描边'
    },
    targetBoxStyle() {
      if (!this.targetBox) return {}
      return {
        left: `${this.targetBox.left * 100}%`,
        top: `${this.targetBox.top * 100}%`,
        width: `${(this.targetBox.right - this.targetBox.left) * 100}%`,
        height: `${(this.targetBox.bottom - this.targetBox.top) * 100}%`
      }
    },
    formattedFileSize() {
      if (!this.file) return '—'
      return this.file.size >= 1024 * 1024
        ? `${(this.file.size / 1024 / 1024).toFixed(1)} MB`
        : `${Math.max(1, Math.round(this.file.size / 1024))} KB`
    }
  },
  mounted() {
    this.loadHistory()
  },
  beforeDestroy() {
    this.releaseUrls()
  },
  methods: {
    handleFileInput(event) {
      const file = event.target.files && event.target.files[0]
      event.target.value = ''
      this.acceptFile(file)
    },
    handleDrop(event) {
      this.dragging = false
      if (this.processing) return
      this.acceptFile(event.dataTransfer.files && event.dataTransfer.files[0])
    },
    acceptFile(file) {
      if (!file) return
      if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
        this.$message.warning('仅支持 JPG、PNG 或 WebP 图片')
        return
      }
      if (file.size > MAX_FILE_BYTES) {
        this.$message.warning('图片不能超过 7MB，请压缩后重新上传')
        return
      }
      const url = URL.createObjectURL(file)
      const image = new Image()
      image.onload = () => {
        if (Math.min(image.naturalWidth, image.naturalHeight) < 128 || Math.max(image.naturalWidth, image.naturalHeight) > 3000) {
          URL.revokeObjectURL(url)
          this.$message.warning('图片短边至少 128px，长边不能超过 3000px')
          return
        }
        this.releaseUrls()
        this.file = file
        this.sourceUrl = url
        this.sourceWidth = image.naturalWidth
        this.sourceHeight = image.naturalHeight
        this.targetBox = null
        this.errorMessage = ''
      }
      image.onerror = () => {
        URL.revokeObjectURL(url)
        this.$message.error('图片无法读取，请重新选择')
      }
      image.src = url
    },
    normalizedPointer(event) {
      const rect = event.currentTarget.getBoundingClientRect()
      if (!rect.width || !rect.height) return null
      return {
        x: Math.min(0.998, Math.max(0.002, (event.clientX - rect.left) / rect.width)),
        y: Math.min(0.998, Math.max(0.002, (event.clientY - rect.top) / rect.height))
      }
    },
    startTargetSelection(event) {
      if (this.resultUrl || this.processing || (event.button != null && event.button !== 0)) return
      const point = this.normalizedPointer(event)
      if (!point) return
      event.preventDefault()
      event.currentTarget.setPointerCapture(event.pointerId)
      this.selectionStart = point
      this.selectionPointerId = event.pointerId
      this.targetBox = { left: point.x, top: point.y, right: point.x, bottom: point.y }
    },
    moveTargetSelection(event) {
      if (this.selectionPointerId !== event.pointerId || !this.selectionStart) return
      const point = this.normalizedPointer(event)
      if (!point) return
      this.targetBox = {
        left: Math.min(this.selectionStart.x, point.x),
        top: Math.min(this.selectionStart.y, point.y),
        right: Math.max(this.selectionStart.x, point.x),
        bottom: Math.max(this.selectionStart.y, point.y)
      }
    },
    finishTargetSelection(event) {
      if (this.selectionPointerId !== event.pointerId || !this.selectionStart) return
      this.moveTargetSelection(event)
      const point = this.normalizedPointer(event) || this.selectionStart
      const minimumWidth = Math.max(12 / this.sourceWidth, 0.03)
      const minimumHeight = Math.max(12 / this.sourceHeight, 0.03)
      if (!this.targetBox
        || this.targetBox.right - this.targetBox.left < minimumWidth
        || this.targetBox.bottom - this.targetBox.top < minimumHeight) {
        this.targetBox = this.defaultTargetBox(point.x, point.y)
      }
      this.selectionStart = null
      this.selectionPointerId = null
      this.errorMessage = ''
    },
    cancelTargetSelection() {
      this.selectionStart = null
      this.selectionPointerId = null
      this.targetBox = null
    },
    defaultTargetBox(x = 0.5, y = 0.5) {
      const halfWidth = 0.2
      const halfHeight = 0.2
      const left = Math.min(0.998 - halfWidth * 2, Math.max(0.002, x - halfWidth))
      const top = Math.min(0.998 - halfHeight * 2, Math.max(0.002, y - halfHeight))
      return { left, top, right: left + halfWidth * 2, bottom: top + halfHeight * 2 }
    },
    handleTargetKeydown(event) {
      if (this.resultUrl || this.processing) return
      if ((event.key === 'Enter' || event.key === ' ') && !this.targetBox) {
        event.preventDefault()
        this.targetBox = this.defaultTargetBox()
        return
      }
      const movement = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }[event.key]
      if (!movement) return
      event.preventDefault()
      const box = this.targetBox || this.defaultTargetBox()
      const step = event.shiftKey ? 0.05 : 0.01
      const dx = Math.min(0.998 - box.right, Math.max(0.002 - box.left, movement[0] * step))
      const dy = Math.min(0.998 - box.bottom, Math.max(0.002 - box.top, movement[1] * step))
      this.targetBox = {
        left: box.left + dx,
        top: box.top + dy,
        right: box.right + dx,
        bottom: box.bottom + dy
      }
    },
    clearTarget() {
      this.targetBox = null
      this.errorMessage = ''
    },
    async cutout() {
      if (!this.file || !this.targetBox || this.processing) return
      this.processing = true
      this.errorMessage = ''
      if (this.resultUrl) {
        URL.revokeObjectURL(this.resultUrl)
        this.resultUrl = ''
      }
      try {
        const form = new FormData()
        form.append('file', this.file, this.file.name || 'cutout-source.png')
        form.append('targetLeft', String(this.targetBox.left))
        form.append('targetTop', String(this.targetBox.top))
        form.append('targetRight', String(this.targetBox.right))
        form.append('targetBottom', String(this.targetBox.bottom))
        const result = await postMultipartResource('/api/images/remove-background', form, { timeoutMs: 150000 })
        this.resultUrl = URL.createObjectURL(result)
        this.$message.success('抠图完成，已生成透明 PNG')
        await this.loadHistory()
      } catch (error) {
        this.errorMessage = error.message || '抠图失败，请稍后重试'
      } finally {
        this.processing = false
      }
    },
    reselect() {
      if (this.resultUrl) URL.revokeObjectURL(this.resultUrl)
      this.resultUrl = ''
      this.errorMessage = ''
    },
    download() {
      if (!this.resultUrl) return
      const original = (this.file && this.file.name ? this.file.name : 'image').replace(/\.[^.]+$/, '')
      const anchor = document.createElement('a')
      anchor.href = this.resultUrl
      anchor.download = `${original}-透明背景.png`
      document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()
    },
    async loadHistory() {
      if (this.historyLoading) return
      this.historyLoading = true
      this.historyError = ''
      try {
        const records = await apiRequest('/api/records?stage=one_click_cutout&limit=12')
        this.history = (Array.isArray(records) ? records : []).map(record => {
          const references = record.inputContext && record.inputContext.referenceImages
          const image = record.images && record.images[0]
          return {
            id: record.id,
            createdAt: record.createdAt,
            source: references && references[0] ? references[0].url : '',
            result: image ? (image.url || image) : '',
            dimensions: image && image.width && image.height ? `${image.width} × ${image.height}` : '原图尺寸'
          }
        })
      } catch (error) {
        this.history = []
        this.historyError = error.message || '抠图记录加载失败，请稍后重试'
      } finally {
        this.historyLoading = false
      }
    },
    previewHistoryImage(record, type) {
      const url = type === 'source' ? record.source : record.result
      if (!url) return
      const items = this.history.reduce((all, item) => {
        if (item.source) all.push({ url: item.source, title: '一键抠图 · 原图', key: `${item.id}-source` })
        if (item.result) all.push({ url: item.result, title: '一键抠图 · 透明 PNG', key: `${item.id}-result` })
        return all
      }, [])
      const index = Math.max(0, items.findIndex(item => item.key === `${record.id}-${type}`))
      this.$emit('preview-image', url, { title: items[index].title, items, index })
    },
    async downloadHistory(record) {
      if (!record.result) return
      try {
        const blob = await downloadResource(record.result)
        const href = URL.createObjectURL(blob)
        const anchor = document.createElement('a')
        anchor.href = href
        anchor.download = `一键抠图-透明背景-${Date.now()}.png`
        document.body.appendChild(anchor)
        anchor.click()
        anchor.remove()
        window.setTimeout(() => URL.revokeObjectURL(href), 1000)
      } catch (error) {
        this.$message.error(error.message || '下载失败')
      }
    },
    isDeletingHistory(id) {
      return this.deletingHistoryIds.includes(id)
    },
    async removeHistory(record) {
      if (!record || !record.id || this.isDeletingHistory(record.id)) return
      try {
        await this.$confirm('删除这条抠图记录后将无法恢复，是否继续？', '删除抠图记录', {
          confirmButtonText: '删除',
          cancelButtonText: '取消',
          type: 'warning'
        })
      } catch (error) {
        return
      }
      this.deletingHistoryIds.push(record.id)
      try {
        await apiRequest(`/api/records/${encodeURIComponent(record.id)}`, { method: 'DELETE' })
        this.history = this.history.filter(item => item.id !== record.id)
        this.$message.success('抠图记录已删除')
      } catch (error) {
        this.$message.error(error.message || '抠图记录删除失败，请稍后重试')
      } finally {
        this.deletingHistoryIds = this.deletingHistoryIds.filter(id => id !== record.id)
      }
    },
    formatTime(value) {
      if (!value) return '刚刚'
      return new Date(value).toLocaleString('zh-CN', { hour12: false })
    },
    reset() {
      this.releaseUrls()
      this.file = null
      this.sourceWidth = 0
      this.sourceHeight = 0
      this.targetBox = null
      this.errorMessage = ''
    },
    releaseUrls() {
      if (this.sourceUrl) URL.revokeObjectURL(this.sourceUrl)
      if (this.resultUrl) URL.revokeObjectURL(this.resultUrl)
      this.sourceUrl = ''
      this.resultUrl = ''
    }
  }
}
</script>

<style scoped>
.cutout-workspace{width:calc(100% - 72px);max-width:1500px;margin:28px auto 60px;color:#202a25}.cutout-hero{display:flex;align-items:flex-end;justify-content:space-between;gap:32px;margin-bottom:20px;border:1px solid #dfe6e1;border-radius:14px;padding:26px 30px;background:linear-gradient(120deg,#fff 0%,#f7faf8 65%,#edf4ff 100%);box-shadow:0 12px 34px rgba(31,45,37,.05)}.cutout-hero p{margin:0 0 7px;color:#2563eb;font-size:10px;font-weight:800;letter-spacing:2px}.cutout-hero h1{margin:0;font-size:30px;line-height:1.2}.cutout-hero>div>span{display:block;margin-top:8px;color:#6f7d75;font-size:13px}.cutout-hero ul{display:flex;flex-wrap:wrap;justify-content:flex-end;gap:10px 18px;margin:0;padding:0;list-style:none;color:#58665e;font-size:12px}.cutout-hero li{white-space:nowrap}.cutout-hero li i{margin-right:4px;color:#229260}.cutout-grid{display:grid;grid-template-columns:minmax(340px,440px) minmax(0,1fr);min-height:650px;overflow:hidden;border:1px solid #dfe5e1;border-radius:14px;background:#fff;box-shadow:0 16px 42px rgba(31,45,37,.07)}.cutout-panel{border-right:1px solid #e2e7e4;padding:28px;background:#fafbfa}.panel-title{display:flex;align-items:center;gap:12px;margin-bottom:18px}.panel-title>span{width:34px;height:34px;display:grid;flex:0 0 34px;place-items:center;border-radius:9px;background:#eaf0ff;color:#2563eb;font-size:12px;font-weight:800}.panel-title div{display:flex;flex-direction:column;gap:4px}.panel-title strong{font-size:15px}.panel-title small{color:#7e8b83;font-size:11px}.cutout-upload{height:300px;overflow:hidden;display:flex;align-items:center;justify-content:center;flex-direction:column;position:relative;border:1.5px dashed #b8c4bc;border-radius:11px;background:#fff;cursor:pointer;transition:border-color .2s,background .2s,box-shadow .2s}.cutout-upload:hover,.cutout-upload.dragging{border-color:#4d7de0;background:#f7f9ff;box-shadow:0 0 0 4px rgba(37,99,235,.08)}.cutout-upload input{width:1px;height:1px;position:absolute;opacity:0}.cutout-upload>i{margin-bottom:12px;color:#3d70d7;font-size:34px}.cutout-upload>strong{font-size:13px}.cutout-upload>small{margin-top:7px;color:#859189;font-size:10px}.cutout-upload.filled{background:#f1f3f2}.cutout-upload img{width:100%;height:100%;display:block;object-fit:contain}.cutout-upload.filled>span{position:absolute;right:10px;bottom:10px;border-radius:7px;padding:8px 11px;background:rgba(25,36,30,.82);color:#fff;font-size:10px;font-weight:700}.source-meta{display:grid;grid-template-columns:repeat(3,1fr);gap:7px;margin-top:12px}.source-meta span{min-width:0;display:flex;flex-direction:column;gap:4px;border:1px solid #e3e8e5;border-radius:8px;padding:9px;background:#fff}.source-meta small{color:#89958e;font-size:9px}.source-meta strong{overflow:hidden;font-size:10px;text-overflow:ellipsis;white-space:nowrap}.privacy-note{display:flex;align-items:center;gap:11px;margin-top:18px;border:1px solid #cfe8da;border-radius:9px;padding:12px 13px;background:#f2fbf6;color:#276746}.privacy-note>i{font-size:18px}.privacy-note span{display:flex;flex-direction:column;gap:3px}.privacy-note strong{font-size:11px}.privacy-note small{color:#57806a;font-size:9px;line-height:1.5}.cutout-submit{width:100%;min-height:58px;display:grid;grid-template-columns:32px 1fr;align-items:center;margin-top:16px;border:0;border-radius:9px;padding:9px 16px;background:#2563eb;color:#fff;box-shadow:0 9px 22px rgba(37,99,235,.22);cursor:pointer;transition:background .2s,transform .2s}.cutout-submit:hover:not(:disabled){background:#1d4ed8;transform:translateY(-1px)}.cutout-submit:focus-visible,.preview-actions button:focus-visible{outline:3px solid rgba(37,99,235,.25);outline-offset:2px}.cutout-submit:disabled{opacity:.62;cursor:not-allowed}.cutout-submit>i{grid-row:1/span 2;font-size:20px}.cutout-submit span{display:flex;align-items:flex-start;flex-direction:column;gap:3px;text-align:left}.cutout-submit strong{font-size:13px}.cutout-submit small{color:rgba(255,255,255,.75);font-size:9px}.cutout-error{display:flex;align-items:flex-start;gap:7px;margin:10px 0 0;border:1px solid #f1c9c5;border-radius:7px;padding:10px;background:#fff5f4;color:#a43e36;font-size:10px;line-height:1.5}.cutout-preview{min-width:0;display:flex;flex-direction:column;padding:28px}.cutout-preview>header{display:flex;align-items:center;justify-content:space-between;gap:20px}.cutout-preview>header>div:first-child{display:flex;flex-direction:column;gap:4px}.cutout-preview header strong{font-size:14px}.cutout-preview header small{color:#829087;font-size:10px}.preview-actions{display:flex;gap:8px}.preview-actions button{height:40px;border:1px solid #d9e1dc;border-radius:7px;padding:0 13px;background:#fff;color:#526159;font-size:10px;cursor:pointer}.preview-actions button.primary{border-color:#2563eb;background:#2563eb;color:#fff}.preview-empty{flex:1;display:flex;align-items:center;justify-content:center;flex-direction:column;min-height:520px;color:#7e8b83;text-align:center}.preview-empty>span{width:68px;height:68px;display:grid;place-items:center;margin-bottom:15px;border-radius:18px;background:#edf2ff;color:#2f66d4;font-size:27px}.preview-empty strong{color:#3b4740;font-size:14px}.preview-empty small{margin-top:7px;font-size:10px}.preview-stage{min-height:520px;display:flex;align-items:center;justify-content:center;position:relative;margin-top:18px;border:1px solid #dde4df;border-radius:11px;background-color:#fff;background-image:linear-gradient(45deg,#e8ecea 25%,transparent 25%),linear-gradient(-45deg,#e8ecea 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#e8ecea 75%),linear-gradient(-45deg,transparent 75%,#e8ecea 75%);background-position:0 0,0 12px,12px -12px,-12px 0;background-size:24px 24px}.preview-stage img{width:100%;height:100%;max-height:560px;display:block;object-fit:contain}.stage-label{position:absolute;top:12px;left:12px;border-radius:999px;padding:7px 10px;background:rgba(31,43,36,.82);color:#fff;font-size:9px;font-weight:700}.stage-label.completed{background:#237a53}.result-summary{display:flex;align-items:center;gap:12px;margin-top:14px;border:1px solid #cfe6d9;border-radius:9px;padding:12px 14px;background:#f3fbf6}.result-summary>span{width:35px;height:35px;display:grid;place-items:center;border-radius:50%;background:#dff4e8;color:#238054;font-size:17px}.result-summary div{display:flex;flex-direction:column;gap:4px}.result-summary strong{font-size:11px}.result-summary small{color:#6d7d74;font-size:9px;line-height:1.45}@media(max-width:980px){.cutout-workspace{width:calc(100% - 28px);margin-top:18px}.cutout-hero{align-items:flex-start;flex-direction:column}.cutout-hero ul{justify-content:flex-start}.cutout-grid{grid-template-columns:1fr}.cutout-panel{border-right:0;border-bottom:1px solid #e2e7e4}.preview-stage{min-height:420px}}@media(max-width:600px){.cutout-workspace{width:calc(100% - 20px)}.cutout-hero,.cutout-panel,.cutout-preview{padding:18px}.cutout-hero h1{font-size:24px}.cutout-hero ul{display:grid}.cutout-upload{height:240px}.source-meta{grid-template-columns:1fr}.cutout-preview>header{align-items:flex-start;flex-direction:column}.preview-actions{width:100%}.preview-actions button{flex:1}.preview-stage{min-height:340px}.result-summary{align-items:flex-start}}@media(prefers-reduced-motion:reduce){.cutout-upload,.cutout-submit{transition:none}}

/* 与“高清放大”等智能改图工具共用同一套页面尺度与卡片语言。 */
.cutout-workspace{width:calc(100% - 68px);max-width:none;margin:26px auto 64px;padding:0;color:#243029}.cutout-grid{grid-template-columns:360px minmax(0,1fr);min-height:610px;overflow:visible;gap:16px;border:0;border-radius:0;background:transparent;box-shadow:none}.cutout-panel,.cutout-preview{border:1px solid #e2e7e3;border-radius:8px;background:#fff;box-shadow:0 10px 28px rgba(37,51,43,.04)}.cutout-panel{border-right:1px solid #e2e7e3;padding:20px;background:#fff}.control-heading{display:flex;align-items:center;gap:12px;margin-bottom:17px}.control-icon{width:38px;height:38px;display:grid;place-items:center;flex:0 0 38px;border-radius:8px;background:#edf4ff;color:#2563eb;font-size:18px}.control-heading>div{display:flex;flex-direction:column;gap:3px}.control-heading strong{font-size:16px}.control-heading small{color:#8a948e;font-size:10px}.cutout-upload{height:230px;border:1px dashed #cdd6d0;border-radius:8px;background:#f8faf8;box-shadow:none}.cutout-upload:hover,.cutout-upload.dragging{border-color:#2563eb;background:#f1f6ff;box-shadow:none}.cutout-upload.filled>span{border-radius:5px;padding:6px 9px}.source-meta{border-radius:7px;padding:10px;background:#f6f8f6}.source-meta span{border:0;padding:0;background:transparent}.privacy-note{margin-top:16px;border:0;border-radius:7px;padding:10px 12px;background:#f5f7f5;color:#637068}.privacy-note small{color:#8a948e}.cutout-submit{min-height:52px;margin-top:15px;border-radius:7px;box-shadow:none;transform:none}.cutout-submit:hover:not(:disabled){transform:none}.cutout-preview{min-height:610px;padding:18px}.cutout-preview>header{padding-bottom:14px}.preview-actions button{height:34px;border-radius:6px;padding:0 11px}.preview-empty{min-height:0;border-radius:8px;background:#f5f7f5}.preview-empty>span{width:60px;height:60px;border-radius:50%;font-size:25px}.preview-stage{min-height:0;flex:1;margin-top:0;border-radius:8px}.result-summary{border-radius:7px}@media(max-width:1100px){.cutout-grid{grid-template-columns:320px minmax(0,1fr)}}@media(max-width:767px){.cutout-workspace{width:calc(100% - 26px);margin-top:18px;padding:0 0 36px}.cutout-grid{grid-template-columns:1fr}.cutout-preview{min-height:520px}.source-meta{grid-template-columns:1fr}.preview-stage{min-height:420px}.cutout-preview>header{align-items:flex-start;flex-direction:column}.preview-actions{width:100%}.preview-actions button{min-height:44px;flex:1}}
.target-guide{display:grid;grid-template-columns:34px minmax(0,1fr) auto;align-items:center;gap:10px;margin-top:14px;border:1px solid #bfd2fa;border-radius:8px;padding:11px;background:#f1f6ff}.target-guide>span{width:34px;height:34px;display:grid;place-items:center;border-radius:50%;background:#dce8ff;color:#2563eb;font-size:17px}.target-guide>div{display:flex;min-width:0;flex-direction:column;gap:3px}.target-guide strong{font-size:12px}.target-guide small{color:#586b91;font-size:10px;line-height:1.45}.target-guide.ready{border-color:#b9dfca;background:#f0faf4}.target-guide.ready>span{background:#d9f1e3;color:#208052}.target-guide.ready small{color:#557866}.target-guide button{min-width:44px;min-height:44px;border:0;border-radius:6px;background:transparent;color:#53655b;font-size:11px;cursor:pointer}.target-guide button:hover{background:rgba(34,80,56,.08)}.target-guide button:focus-visible,.target-surface:focus-visible{outline:3px solid rgba(37,99,235,.3);outline-offset:3px}.privacy-note{margin-top:14px}.cutout-submit{min-height:56px}.preview-actions button{min-height:44px}.preview-stage{padding:18px;overflow:hidden}.target-surface{max-width:100%;max-height:560px;display:inline-flex;position:relative;line-height:0;touch-action:manipulation}.target-surface.selectable{cursor:crosshair}.preview-stage .target-surface img{width:auto;height:auto;max-width:100%;max-height:560px;display:block;object-fit:contain;user-select:none;-webkit-user-drag:none}.target-marker{width:34px;height:34px;position:absolute;border:2px solid #fff;border-radius:50%;background:#2563eb;box-shadow:0 0 0 3px rgba(37,99,235,.38),0 3px 12px rgba(15,39,88,.36);transform:translate(-50%,-50%);pointer-events:none}.target-marker::before,.target-marker::after{content:"";position:absolute;left:50%;top:50%;background:#fff;transform:translate(-50%,-50%)}.target-marker::before{width:16px;height:2px}.target-marker::after{width:2px;height:16px}.target-marker i{position:absolute;inset:-8px;border:2px solid rgba(37,99,235,.42);border-radius:50%;animation:targetPulse 1.8s ease-out infinite}.stage-label{max-width:calc(100% - 24px);line-height:1.35}@keyframes targetPulse{0%{opacity:.85;transform:scale(.75)}70%,100%{opacity:0;transform:scale(1.35)}}@media(max-width:767px){.preview-stage{padding:10px}.target-surface,.preview-stage .target-surface img{max-height:500px}}@media(prefers-reduced-motion:reduce){.target-marker i{animation:none}}
.target-surface{touch-action:none}.target-marker{width:auto;height:auto;min-width:12px;min-height:12px;border:2px solid #2563eb;border-radius:5px;background:rgba(37,99,235,.14);box-shadow:0 0 0 1px #fff,0 5px 18px rgba(20,48,105,.24);transform:none}.target-marker::before,.target-marker::after{content:none}.target-marker>span{position:absolute;left:-2px;top:-29px;border-radius:5px 5px 5px 0;padding:5px 8px;background:#2563eb;color:#fff;font-size:10px;font-weight:700;line-height:1;white-space:nowrap}.target-marker i{width:10px;height:10px;inset:auto -6px -6px auto;border:2px solid #fff;border-radius:50%;background:#2563eb;box-shadow:0 0 0 1px #2563eb;animation:none}
.cutout-history{margin-top:16px;border:1px solid #e2e8e3;border-radius:11px;padding:18px;background:#fff;box-shadow:0 8px 26px rgba(29,43,35,.05)}
.cutout-history-head{display:flex;align-items:center;justify-content:space-between;gap:16px;padding-bottom:14px}.cutout-history-head>div{display:flex;flex-direction:column;gap:3px}.cutout-history-head strong{font-size:14px}.cutout-history-head small{color:#87918b;font-size:10px}.cutout-history-head>button{min-height:44px;display:inline-flex;align-items:center;gap:6px;border:1px solid #cbdaf4;border-radius:6px;padding:0 12px;background:#f5f8ff;color:#255fc3;font-size:10px;cursor:pointer}.cutout-history-head>button:disabled{opacity:.65;cursor:wait}.cutout-history-head>button:focus-visible,.cutout-history-grid button:focus-visible{outline:3px solid rgba(37,99,235,.25);outline-offset:2px}
.cutout-history-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px}.cutout-history-grid article{min-width:0;overflow:hidden;border:1px solid #e2e7e3;border-radius:8px;background:#fff}.history-comparison{display:grid;grid-template-columns:1fr 1fr;gap:1px;position:relative;background:#dfe5e1}.history-comparison>button{min-width:0;aspect-ratio:16/10;overflow:hidden;position:relative;display:grid;place-items:center;border:0;padding:0;background:#f2f4f2;color:#7e8982;cursor:zoom-in}.history-comparison>button:disabled{cursor:not-allowed}.history-comparison img{width:100%;height:100%;display:block;object-fit:contain}.history-comparison>button>span{display:flex;align-items:center;justify-content:center;gap:5px;padding:8px;color:#7e8982;font-size:10px;text-align:center}.history-comparison>button>small{position:absolute;left:7px;bottom:7px;border-radius:4px;padding:4px 6px;background:rgba(28,39,33,.78);color:#fff;font-size:9px}.history-comparison>button:last-of-type{background-color:#fff;background-image:linear-gradient(45deg,#e8ecea 25%,transparent 25%),linear-gradient(-45deg,#e8ecea 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#e8ecea 75%),linear-gradient(-45deg,transparent 75%,#e8ecea 75%);background-position:0 0,0 8px,8px -8px,-8px 0;background-size:16px 16px}.history-comparison>button:last-of-type>small{background:rgba(37,99,235,.88)}.history-comparison>em{position:absolute;z-index:2;top:8px;right:8px;border-radius:12px;padding:4px 7px;background:#e8f6ed;color:#277447;font-size:9px;font-style:normal}.history-dimension{min-height:38px;display:flex;align-items:center;gap:7px;border-top:1px solid #e2e7e3;border-bottom:1px solid #e7ebe8;padding:0 11px;background:#f8faf9;color:#7b8780;font-size:10px}.history-dimension>i{color:#5076b9;font-size:12px}.history-dimension>strong{margin-left:auto;color:#34443b;font-size:10px;font-variant-numeric:tabular-nums}.cutout-history-grid footer{min-height:62px;display:flex;align-items:center;justify-content:space-between;gap:10px;padding:9px 10px}.cutout-history-grid footer>div{min-width:0;display:flex;flex-direction:column;gap:4px}.cutout-history-grid footer strong{overflow:hidden;color:#39463e;font-size:11px;text-overflow:ellipsis;white-space:nowrap}.cutout-history-grid footer small{color:#8b958f;font-size:9px}.cutout-history-grid footer>button{width:44px;height:44px;flex:0 0 44px;border:1px solid #dce3de;border-radius:6px;background:#fff;color:#255fc3;cursor:pointer}.cutout-history-grid footer>button:disabled{color:#adb6b0;cursor:not-allowed}.cutout-history-empty{min-height:170px;display:flex;align-items:center;justify-content:center;flex-direction:column;gap:7px;border:1px dashed #d4dad6;border-radius:8px;background:#fafbfa;color:#8b958f;text-align:center}.cutout-history-empty>i{color:#7896cf;font-size:28px}.cutout-history-empty strong{color:#536058;font-size:12px}.cutout-history-empty small{font-size:10px}
.cutout-history-grid footer>.history-actions{display:flex;align-items:center;flex-direction:row;gap:8px}.history-actions button{width:44px;height:44px;flex:0 0 44px;border:1px solid #dce3de;border-radius:6px;background:#fff;color:#255fc3;cursor:pointer}.history-actions button.danger{border-color:#f0d3d3;color:#c43e3e}.history-actions button:hover:not(:disabled){background:#f5f8ff}.history-actions button.danger:hover:not(:disabled){background:#fff2f2}.history-actions button:disabled{color:#adb6b0;cursor:not-allowed}.history-actions button.danger:disabled{border-color:#e5dddd;color:#b9abab}
@media(max-width:1100px){.cutout-history-grid{grid-template-columns:repeat(2,minmax(0,1fr))}}@media(max-width:767px){.cutout-history{padding:14px}.cutout-history-head{align-items:stretch;flex-direction:column}.cutout-history-head>button{justify-content:center}.cutout-history-grid{grid-template-columns:1fr}}
</style>
